/*
 * Purchasing kill switch — the two things Liquid can't do server-side.
 *
 * 1. Cart auto-removal: when a product's `custom.purchase_paused` metafield is on
 *    and it's already in the cart, remove the line item(s) and surface a notice.
 *    Driven by window.iwPurchasePausedCart (variant ids rendered from the cart's
 *    own metafields, present on every page) plus a /cart.js handle check.
 * 2. Boost grid gating: Boost rebuilds collection/search cards in JS from a
 *    mustache template, so there's no server HTML for Liquid to hide. Disable
 *    `.productCard__buy` whose data-product-handle is in the paused set (seeded
 *    server-side into window.iwPurchasePaused.handles). When the restock notify
 *    button is disabled (hideQuickviewWhenPaused), also hide the paused card's
 *    quick-view trigger, since the modal would offer no action.
 *
 * Every server-rendered surface (PDP, Liquid cards, quickview) is already gated
 * in Liquid — this module only covers what Liquid cannot reach.
 */
(function () {
  'use strict';

  if (window.iwPurchasePausedInit) return;
  window.iwPurchasePausedInit = true;

  var config = window.iwPurchasePaused || { handles: [], message: '' };
  var pausedHandles = {};
  (config.handles || []).forEach(function (handle) {
    if (handle) pausedHandles[handle] = true;
  });

  var PAUSED_LABEL = 'Unavailable';

  function toArray(nodeList) {
    return Array.prototype.slice.call(nodeList || []);
  }

  /* ---- Cart auto-removal ---- */

  // Returns the paused line items' unique keys plus their product names. We key
  // removal by line-item key, not variant id, because one-time + subscription
  // lines of the same product share a variant id but are distinct line items —
  // keying by variant id would collapse them and only remove one. Names are
  // deduped by product so those two lines don't name the product twice.
  function collectPaused(cart) {
    var seededIds = window.iwPurchasePausedCart || [];
    var keys = [];
    var names = [];
    (cart.items || []).forEach(function (item) {
      var byHandle = pausedHandles[item.handle] === true;
      var bySeed = seededIds.indexOf(item.variant_id) !== -1 || seededIds.indexOf(item.id) !== -1;
      if ((byHandle || bySeed) && item.key) {
        keys.push(item.key);
        var name = item.product_title || item.title;
        if (name && names.indexOf(name) === -1) names.push(name);
      }
    });
    return { keys: keys, names: names };
  }

  function formatNames(names) {
    if (names.length === 1) return names[0];
    if (names.length === 2) return names[0] + ' and ' + names[1];
    return names.slice(0, -1).join(', ') + ', and ' + names[names.length - 1];
  }

  function buildRemovalMessage(names) {
    if (!names.length) return config.message || '';
    var verb = names.length === 1 ? 'was' : 'were';
    return formatNames(names) + ' ' + verb + ' removed because purchasing is temporarily paused.';
  }

  function showRemovalNotice(names) {
    var notice = document.querySelector('[data-purchase-paused-notice]');
    if (!notice) return;
    notice.textContent = buildRemovalMessage(names);
    notice.removeAttribute('hidden');
  }

  function refreshCart() {
    if (typeof getCartData === 'function') {
      try { getCartData(); return; } catch (e) { /* fall through */ }
    }
    if (window.jQuery) {
      window.jQuery.getJSON('/cart.js', function (cart) {
        if (typeof cartInfo === 'function') cartInfo(cart);
      });
    }
  }

  // Open the cart drawer so the removal notice is actually seen — it lives inside
  // the drawer, which is closed by default. Adds the classes directly (not the
  // theme's toggle) so an already-open drawer stays open. No-op where there's no
  // drawer (e.g. the /cart page).
  function openCartDrawer() {
    var sidebar = document.getElementById('CartSidebar');
    if (!sidebar) return;
    sidebar.classList.add('active');
    var overlay = document.getElementById('cart_overlay');
    if (overlay) overlay.classList.add('active');
    document.documentElement.classList.add('mini-cart-open');
  }

  function removePaused(paused) {
    if (!paused.keys.length) return;
    var updates = {};
    paused.keys.forEach(function (key) { updates[key] = 0; });
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ updates: updates })
    })
      .then(function (res) { return res.json(); })
      .then(function () {
        window.iwPurchasePausedCart = [];
        showRemovalNotice(paused.names);
        refreshCart();
        openCartDrawer();
      })
      .catch(function (err) { console.warn('purchase-paused: cart removal failed', err); });
  }

  function sweepCart() {
    var seededIds = (window.iwPurchasePausedCart || []).filter(Boolean);
    if (!seededIds.length && !Object.keys(pausedHandles).length) return;
    fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) { removePaused(collectPaused(cart)); })
      .catch(function (err) { console.warn('purchase-paused: cart read failed', err); });
  }

  /* ---- Boost grid card gating ---- */

  function gateCards() {
    if (!Object.keys(pausedHandles).length) return;
    // data-prod is unique to the card buy button (the quickview link has data-render
    // instead), so we target it by data attribute rather than a styling class.
    toArray(document.querySelectorAll('[data-prod][data-product-handle]')).forEach(function (btn) {
      if (btn.hasAttribute('data-purchase-paused')) return;
      var handle = btn.getAttribute('data-product-handle');
      if (!handle || pausedHandles[handle] !== true) return;
      btn.setAttribute('disabled', 'disabled');
      btn.setAttribute('data-purchase-paused', '');
      btn.classList.add('productCard__buy--oos');
      btn.textContent = PAUSED_LABEL;
    });
    if (!config.hideQuickviewWhenPaused) return;
    // The quickview trigger carries data-render (the buy button doesn't), so it's
    // targetable without the styling class.
    toArray(document.querySelectorAll('[data-render][data-product-handle]')).forEach(function (link) {
      if (link.hasAttribute('data-purchase-paused-quickview')) return;
      var handle = link.getAttribute('data-product-handle');
      if (!handle || pausedHandles[handle] !== true) return;
      link.style.display = 'none';
      link.setAttribute('data-purchase-paused-quickview', '');
    });
  }

  function watchForCards() {
    if (!Object.keys(pausedHandles).length || !window.MutationObserver) return;
    var scheduled = false;
    var observer = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        gateCards();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ---- Mini-cart quick view ---- */

  // Relabel the shared mini-cart Add button after the vendor handler populates it
  // (it stops propagation, so we run in capture phase + a deferred callback). The
  // vendor already disables it via data-available="paused"; we only fix the label.
  // Bound site-wide since recommendations are cart-driven, not page-render-driven.
  function watchMiniCartQuickAdd() {
    document.addEventListener('click', function (e) {
      var trigger = e.target && e.target.closest ? e.target.closest('.js__quick-view-mini-cart-click') : null;
      if (!trigger || trigger.getAttribute('data-available') !== 'paused') return;
      setTimeout(function () {
        var btn = document.querySelector('.modal-quick-view-mini-cart .js__addon-add-to-cart');
        if (!btn) return;
        btn.setAttribute('disabled', 'disabled');
        btn.textContent = PAUSED_LABEL;
      }, 0);
    }, true);
  }

  /* ---- Init ---- */

  function init() {
    sweepCart();
    gateCards();
    watchForCards();
    watchMiniCartQuickAdd();
    if (window.jQuery) {
      window.jQuery(document).on('cart.requestComplete', function () {
        sweepCart();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
